import {useTranslation} from 'react-i18next';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import React, {useEffect, useRef, useState} from 'react';
import {PaymentRequest, Stripe, StripeElementsOptions} from '@stripe/stripe-js';
import {useQueryToken, useValueParams} from '../../utils/dynamicCodes/hooks';
import {useHistory} from 'react-router-dom';
import Analytics from '../../utils/Analytics';
import Environment from '../../utils/Environment';
import usePaywallDismissTracking from '../../hooks/usePaywallDismissTracking';
import usePaywallAnalytics from '../../hooks/usePaywallAnalytics';
import {PaywallSlug} from '../../analytics/paywallEvents';
import {
  createCheckoutDetails,
  createPaymentRequest,
  createStripeInstance,
} from '../emailConfirmation-0424/setupStripeForEmailConfirmationPaywall';
import {reportRepeatCheckout} from '../../utils/dynamicCodes/redirects';
import validateEmail from '../../utils/validateEmail';
import * as Sentry from '@sentry/react';
import {Helmet} from 'react-helmet';
import {Elements} from '@stripe/react-stripe-js';
import PaymentIntentScreen from '../../screens/StripeCustomCheckout/PaymentIntentScreen';
import MaybeStagingBadge from '../../components/MaybeStagingBadge';
import {allowedLocale} from '../emailConfirmation-0424/screens/EmailConfirmationCheckoutScreen';
import {RiseRadioButton} from '../../components/RiseRadioButton';
import {
  ExpiredCheckoutScreenProps,
  ExpiredPaywallProductCode,
  getExpiredPaywallProductConfigs,
} from '../expiredPaywall/prices';
import {SatisfactionModal} from '../../onboarding-v3/components';
import './TrialCancelPaywall.css';
import {growthBook} from '../../index';
import {tokenParam} from '../../utils/linkOutSession';

// Identifies this paywall on the shared low-cardinality `[Web Payments] Paywall *` events.
const PAYWALL: PaywallSlug = 'trial-cancel';

const TrialCancelPaywall = (props: ExpiredCheckoutScreenProps) => {
  const {t, i18n} = useTranslation(); // will ensure the correct translation is used in `getEmailConfirmationProducts`
  const {initialOption, otherOption} = props;
  useDocumentTitle('Rise');
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const token = useQueryToken();
  const localeParam: string = useValueParams('locale') ?? 'US';
  const locale = allowedLocale(localeParam);
  const history = useHistory();
  // const [trialOption, setTrialOption] = useState<ProductCode>(initialOption)
  // const emailConfirmationProducts = getEmailConfirmationProducts()
  // const product = emailConfirmationProducts[trialOption][locale]

  const [options, setOptions] = useState<StripeElementsOptions>({});
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | undefined>(undefined);
  const [paymentRequestLoading, setPaymentRequestLoading] = useState<boolean>(true);
  const [clientSecret, setClientSecret] = useState<string>('');
  const [duplicateCustomer, setDuplicateCustomer] = useState<boolean>(false);
  const [chargeDate, setChargeDate] = useState<string>('');

  const [selectedPlan, setSelectedPlan] = useState<ExpiredPaywallProductCode>(initialOption);

  const expiredPaywallProducts = getExpiredPaywallProductConfigs();
  const planAnnual = expiredPaywallProducts[initialOption][locale];
  const planMonthly = expiredPaywallProducts[otherOption][locale];
  const selectedProduct = expiredPaywallProducts[selectedPlan][locale];

  const [showModal, setShowModal] = useState<boolean>(false);

  const paywallAnalytics = usePaywallAnalytics({
    paywall: PAYWALL,
    baseProps: {email: token, locale},
    legacy: {
      viewed: 'Trial Cancel Web Paywall Viewed',
      trialSelection: 'Trial Cancel Trial Selection',
      purchaseSuccess: 'Trial Cancel Purchase Success',
    },
  });

  const {markPurchased} = usePaywallDismissTracking({
    paywall: PAYWALL,
    properties: {email: token, locale, selectedPlan, code: selectedProduct.code},
  });

  // Stripe setup
  useEffect(() => {
    const setupStripe = async () => {
      if (token && !stripeInstance) {
        const checkoutDetails = await createCheckoutDetails(token, true);
        if ('error' in checkoutDetails) {
          if (checkoutDetails.duplicateCustomer) {
            setDuplicateCustomer(true);
          } else {
            Analytics.track('Unable to set up Stripe Custom Checkout Page', {
              env: Environment.getVar('REACT_APP_ENV') ?? 'test',
              email: token,
            });
          }
        } else {
          const stripe = await createStripeInstance();
          if (stripe) {
            setStripeInstance(stripe);
          }
          setClientSecret(checkoutDetails.client_secret);
          setChargeDate(checkoutDetails.charge_date);
          setOptions(checkoutDetails.options);
        }
      }
    };
    if (token) {
      setupStripe();
    }
  }, [stripeInstance, token]);

  // Fire analytics for user who has a duplicate customer
  useEffect(() => {
    if (duplicateCustomer) {
      reportRepeatCheckout(selectedProduct.code, token);
    }
  }, [duplicateCustomer, selectedProduct.code, token]);

  // Create Payment Request
  useEffect(() => {
    if (stripeInstance) {
      if (paymentRequest) {
        paymentRequest.update({
          total: {
            label: selectedPlan === planAnnual.productCode ? t('expired.annual_plan') : t('expired.monthly_plan'),
            amount: selectedProduct.price,
          },
        });
      } else {
        createPaymentRequest(
          stripeInstance,
          selectedProduct.code,
          selectedProduct.price,
          selectedPlan === planAnnual.productCode ? t('expired.annual_plan') : t('expired.monthly_plan'),
          locale,
          PAYWALL,
        ).then(paymentRequest => {
          setPaymentRequest(paymentRequest);
          setPaymentRequestLoading(false);
        });
      }
    }
  }, [
    paymentRequest,
    selectedProduct.code,
    selectedProduct.price,
    selectedProduct.sheetLabel,
    stripeInstance,
    selectedPlan,
    locale,
    planAnnual.productCode,
    t,
  ]);

  const handlePlanSelection = (plan: ExpiredPaywallProductCode) => {
    paywallAnalytics.trialSelection({trialOption: plan});
    setSelectedPlan(plan);
  };

  const handlePurchaseSuccess = ({existingAccount}: {existingAccount: boolean}) => {
    markPurchased();
    paywallAnalytics.purchaseSuccess({trialOption: selectedPlan});
    // Redirect to the new updated success screen instead of trial screen
    history.push(`/update-success?${tokenParam(token)}lang=${i18n.language}`);
  };

  const refToScroll = useRef<HTMLDivElement>(null);

  const onCreditCardChoice = () => {
    refToScroll.current?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const tokenIsLikelyAnId = token && !validateEmail(token);
    if (token && tokenIsLikelyAnId) {
      try {
        Analytics.identify(Number(token));
        Analytics.track('Identified user via token', {token: Number(token)});
      } catch (error) {
        Analytics.track('Unable to identify user via token', {token});
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
      }
    }
    paywallAnalytics.viewed({language: i18n.language});
  }, [token, i18n.language, paywallAnalytics]);

  return (
    <>
      <Helmet>
        {/* Prevents zooming into the text input when entering a credit card number */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      </Helmet>
      <div className={`w-full h-screen mx-auto checkout-screen-background`}>
        <a
          href="https://risesciencehelp.zendesk.com/hc/en-us"
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-4 right-4 z-20 text-white hover:opacity-80 transition-opacity font-bold"
          onClick={() => Analytics.track('Help Clicked')}
        >
          Help
        </a>
        <div>
          <div
            style={{
              background: 'linear-gradient(180deg, #FF6B35 0%, #7C3AED 100%)',
              paddingTop: '6vh',
              paddingBottom: '5vh',
            }}
          >
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  borderRadius: 12,
                  marginBottom: 18,
                  paddingLeft: 10,
                  paddingRight: 10,
                  paddingTop: 2,
                  paddingBottom: 2,
                  gap: 10,
                }}
              >
                <div>
                  <img src="/price_drop_arrow_percentage_yellow2.png" alt="Arrow down" style={{height: '18px'}} />
                </div>
                <div>
                  <p style={{color: 'yellow', fontWeight: 800, fontSize: '14px'}}>Lower price</p>
                </div>
              </div>
            </div>
            <div style={{display: 'flex', justifyContent: 'center'}}>
              <img src="/app-icon.png" alt="Rise Logo" className="w-24" />
            </div>
            <h1
              style={{
                color: 'white',
                fontSize: '28px',
                textAlign: 'center',
                paddingLeft: '15px',
                paddingRight: '15px',
              }}
            >
              Better Sleep, Better Price
            </h1>
            <h2
              style={{
                color: 'white',
                fontSize: '16px',
                textAlign: 'center',
                paddingLeft: '15px',
                paddingRight: '15px',
              }}
            >
              We won't let cost stand in the way of better sleep & energy. Get RISE at 50% off
            </h2>
          </div>
        </div>

        <div
          ref={refToScroll}
          style={{
            position: 'absolute',
            left: 0,
            top: '25vh',
            backgroundColor: 'transparent',
            height: '1px',
            width: '100%',
          }}
        />
        <div
          className={`checkout-screen-content`}
          style={{top: 0, background: 'linear-gradient(180deg, #33106F 0%, #333333 60%)'}}
        >
          <div style={{minHeight: window.innerHeight}} className={`h-screen w-full`}>
            <div className="flex flex-col">
              <div>
                <div style={{backgroundColor: '#8E58FF', paddingLeft: '10px'}}>
                  <p style={{color: 'white'}}>{'CHOOSE YOUR PLAN'}</p>
                </div>
                <div className={'cell--container'}>
                  <button
                    onClick={() => {
                      handlePlanSelection(planAnnual.productCode);
                    }}
                    className={'cell--bodyContainer'}
                    style={{background: 'transparent', justifyContent: 'space-between'}}
                  >
                    <div style={{marginTop: '6px', marginBottom: '6px'}}>
                      <div className={'cell--title'} style={{fontSize: '18px', marginBottom: '5px'}}>
                        Best deal
                      </div>
                      <div className={'cell--subtitle'} style={{fontSize: '18px', fontWeight: 600}}>
                        <span style={{textDecoration: 'line-through'}}>
                          {getExpiredPaywallProductConfigs()['0d_annual_70'][locale].annualPrice}
                        </span>{' '}
                        <span style={{color: 'gold'}}>{planAnnual.annualPrice}/year</span>
                      </div>
                    </div>
                    <div style={{marginLeft: '30px'}}>
                      <div className={'discount-badge'}>Save 50%</div>
                    </div>
                    <RiseRadioButton
                      checked={selectedPlan === planAnnual.productCode}
                      onChange={() => {
                        handlePlanSelection(planAnnual.productCode);
                      }}
                    />
                  </button>
                </div>
                <div className={'cell--container'}>
                  <div style={{paddingTop: '6px'}}>
                    <div style={{display: 'flex', flexDirection: 'column'}}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'start',
                          gap: '3px',
                          paddingLeft: '15px',
                          paddingRight: '24px',
                          justifyContent: 'flex-start',
                          width: '100%',
                          marginBottom: '12px',
                          marginTop: '12px',
                        }}
                      >
                        <div
                          style={{
                            minWidth: '36px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <img
                            src={'/yellow-thumbup.svg'}
                            alt={'Thumbs Up'}
                            style={{
                              backgroundColor:
                                'invert(85%) sepia(27%) saturate(4162%) hue-rotate(359deg) brightness(109%) contrast(99%)',
                            }}
                          />
                        </div>
                        <p style={{color: 'white', fontWeight: 500, fontSize: '14px'}}>
                          <span style={{color: 'gold', fontWeight: 600}}>That's {planAnnual.perMonth}/mo</span> for
                          better sleep & energy
                        </p>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'start',
                          gap: '3px',
                          paddingLeft: '15px',
                          paddingRight: '24px',
                          justifyContent: 'flex-start',
                          width: '100%',
                          marginBottom: '10px',
                        }}
                      >
                        <div
                          style={{
                            minWidth: '36px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <img src={'/yellow-badge.svg'} alt={'Badge'} />
                        </div>
                        {showModal ? <SatisfactionModal onClose={() => setShowModal(false)} /> : null}
                        <p style={{fontWeight: 500, color: 'white', fontSize: '14px'}}>
                          <span onClick={() => setShowModal(true)}>
                            <span style={{color: 'gold', fontWeight: 600}}>Money back guarantee</span> if you're not
                            feeling a difference
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={'cell--container'}>
                  <button
                    onClick={() => {
                      handlePlanSelection(planMonthly.productCode);
                    }}
                    className={'cell--bodyContainer'}
                    style={{background: 'transparent', minHeight: '10px'}}
                  >
                    <div>
                      <div className={'cell--title'}>{t('expired.per_month', {quantity: planMonthly.perMonth})}</div>
                    </div>
                    <RiseRadioButton
                      checked={selectedPlan === planMonthly.productCode}
                      onChange={() => {
                        handlePlanSelection(planMonthly.productCode);
                      }}
                    />
                  </button>
                </div>
              </div>
              <div>
                {duplicateCustomer ? (
                  <div className={'text-white'}>Your subscription is active!</div>
                ) : Object.keys(options).length && stripeInstance ? (
                  <div className={'pl-5 pr-5'}>
                    <Elements stripe={stripeInstance} options={options}>
                      <PaymentIntentScreen
                        code={selectedProduct.code}
                        paywall={PAYWALL}
                        paymentRequest={paymentRequest}
                        clientSecret={clientSecret}
                        daysFree={selectedProduct.daysFree}
                        chargeDate={chargeDate}
                        dynamicCheckout={true}
                        onPurchaseSuccess={handlePurchaseSuccess}
                        paymentRequestLoading={paymentRequestLoading}
                        onCreditCardChoice={onCreditCardChoice}
                        ctaText={selectedProduct === planAnnual ? 'REDEEM 50% OFF' : 'REDEEM MONTHLY PLAN'}
                        allowRepeatPurchase={true}
                        useUpdatedCreditCardLogic={true}
                        useCustomCTA={growthBook.getFeatureValue('custom_cta_paywalls', ['']).includes('trial_cancel')}
                        customCTAText={selectedProduct === planAnnual ? 'REDEEM 50% OFF' : 'REDEEM MONTHLY PLAN'}
                      />
                    </Elements>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <MaybeStagingBadge />
      </div>
    </>
  );
};

export default TrialCancelPaywall;
